import { Box, Center, Divider, Fade, Flex, HStack, Icon, IconButton, Image, Link, Menu, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, Radio, Skeleton, SkeletonCircle, Spacer, Stack, Text, Tooltip, VStack, useColorModeValue as mode, useDisclosure, useMenuButton } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import React, { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { BiLock, BiLogOut, BiUserCircle } from "react-icons/bi";
import { HiOutlineMenuAlt1, HiOutlineTranslate, HiSelector } from "react-icons/hi";
import { useHistory, useLocation } from "react-router-dom";
import { checkIsActive, getCurrentUrl, toAbsoluteUrl, useLocalStorage } from "../helpers";
import { useAuth } from "../helpers/configs/plugins/CookieAuthPlugin";
import { menuItems } from "../helpers/constants/menu";
import { APP_VERSION } from "../helpers/environment";
import { dropAnimation } from "./styles";

const SidebarContext = createContext("SidebarContext");
const MotionBox = motion(Box);

export default function Sidebar() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [collapse, setCollapse] = useLocalStorage("sidebar_toggle", false);
  const { user, logout } = useAuth();

  const getMenuItemActive = (url) => {
    if (location.key && location.pathname !== "/") {
      return checkIsActive(location, url);
    }
  };

  return (
    <SidebarContext.Provider value={{ user, logout, collapse }}>
      <Flex zIndex="overlay" w="full" h="full" direction="column" borderRightWidth="1px" transition="width 0.1s" width={`${collapse ? "75px" : "72"}`}>
        <Flex overflow="hidden" w="full" h="full" direction="column" flex="1" py="4" overflowY="auto" px="4">
          <Flex w="full" position="relative" justifyContent="center">
            <Fade in={!collapse}>
              <Center cursor="pointer" onClick={() => history.push("/")}>
                <VStack spacing="0">
                  <Image h="7rem" src={toAbsoluteUrl("/assets/logo.png")} />
                  <Text hidden={collapse} fontSize="xs" mx="auto" bg="transparent" cursor="pointer" color="transparent" _hover={{ color: "brand.200" }} fontWeight="bold">
                    {t("version") + " " + APP_VERSION}
                  </Text>
                </VStack>
              </Center>
            </Fade>
            <IconButton pos="absolute" right="0" top="0" _focus={{ outline: "none" }} variant="ghost" colorScheme="gray" onClick={() => setCollapse((prevCollapse) => !prevCollapse)} icon={<HiOutlineMenuAlt1 size="1.5rem" />} />
          </Flex>

          <AnimatePresence mode="wait">
            <Stack spacing="2" as="nav" aria-label="Sidebar Navigation">
              {menuItems.map(
                ({ name, item }, i) =>
                  !item.every((menuItem) => user?.menuPermission && user?.menuPermission[menuItem.id]?.["VIEW"] !== true) && (
                    <MotionBox {...dropAnimation} key={name}>
                      <Text whiteSpace="nowrap" px="3" fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="widest" color="gray.500">
                        {name && <Fade in={!collapse}>{t(`label.${name}`)}</Fade>}
                      </Text>
                      <Stack mt="2" spacing="1">
                        {item.map(({ id, name, icon, path }) => {
                          const hasPermission = (user?.menuPermission && user.menuPermission[id]?.["VIEW"]) || false;
                          return hasPermission && <NavLink key={id} collapse={collapse} isActive={getMenuItemActive(path)} onClick={() => path !== getCurrentUrl(location) && history.push(path)} icon={icon} label={t(`label.${name}`)} />;
                        })}
                      </Stack>
                      {menuItems.length - 1 > i && <Divider bg="gray.100" rounded="md" />}
                    </MotionBox>
                  ),
              )}
            </Stack>
          </AnimatePresence>
          <Spacer />
        </Flex>
        <UserProfile />
      </Flex>
    </SidebarContext.Provider>
  );
}

const UserProfile = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { username } = useAuth()?.user || {};
  const { collapse, logout } = useContext(SidebarContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const handleChangeLanguage = (language) => {
    i18n.changeLanguage(language);
    onClose();
  };

  const languages = [
    { code: "kh", label: "ភាសាខ្មែរ", flag: toAbsoluteUrl("/assets/flag-kh.png") },
    { code: "en", label: "English", flag: toAbsoluteUrl("/assets/flag-en.png") },
    { code: "cn", label: "中国人", flag: toAbsoluteUrl("/assets/flag-cn.png") },
  ];

  return (
    <>
      <Modal isCentered onClose={onClose} isOpen={isOpen} closeOnOverlayClick={false} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text fontSize="lg" fontWeight="bold">
              {t("language")}
            </Text>
          </ModalHeader>
          <ModalCloseButton _focus={{ outline: "none" }} />
          <ModalBody>
            <VStack spacing={4} align="start" pb="4">
              {languages.map((language) => (
                <HStack w="full" justifyContent="space-between" key={language.code} cursor="pointer" onClick={() => handleChangeLanguage(language.code)}>
                  <HStack spacing="3">
                    <Image boxSize="40px" borderRadius="full" src={language.flag} alt="Cambodia Flag" objectFit="fit" />
                    <Text fontSize="md">{language.label}</Text>
                  </HStack>
                  <Spacer />
                  <Radio isChecked={i18n.language === language.code} onChange={() => handleChangeLanguage(language.code)} />
                </HStack>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <HStack spacing="4" px="2" flexShrink={0} borderTopWidth="1px" p="4">
        <Menu>
          <AccountSwitcherButton collapse={collapse} />
          <MenuList shadow="lg" py="2" color={mode("gray.600", "gray.300")} px="2">
            <MenuItem rounded="md" onClick={() => onOpen()}>
              <Flex alignItems="center" gap="2" w="full">
                <Icon as={HiOutlineTranslate} />
                <Flex justifyContent="space-between" w="full">
                  <Text>{t("language")}</Text>
                  <Text opacity=".6">{t("language")}</Text>
                </Flex>
              </Flex>
            </MenuItem>
            {username !== "super_admin" && (
              <MenuItem rounded="md" onClick={() => history.push("/change-password")}>
                <Flex alignItems="center" gap="2">
                  <Icon as={BiLock} />
                  <Text>{t("label.change_password")}</Text>
                </Flex>
              </MenuItem>
            )}
            <MenuItem rounded="md" onClick={() => logout()}>
              <Flex alignItems="center" gap="2" color="redAlt.500">
                <Icon as={BiLogOut} />
                <Text>{t("label.logout")}</Text>
              </Flex>
            </MenuItem>
          </MenuList>
        </Menu>
      </HStack>
    </>
  );
};

const AccountSwitcherButton = () => {
  const { collapse, user } = useContext(SidebarContext);
  const buttonProps = useMenuButton();

  return (
    <Flex as="button" {...buttonProps} w="full" display="flex" justifyContent="center" alignItems="center" rounded="lg" bg={mode("gray.200", "gray.600")} p="2" fontSize="md" userSelect="none" cursor="pointer" outline="0" transition="all 0.2s" _active={{ bg: mode("gray.200", "gray.600") }} _focus={{ shadow: "outline" }}>
      <HStack flex="1" spacing="3">
        <>
          {user.firstName ? (
            <>
              <Image h="8" w="8" borderRadius="md" objectFit="cover" src={user.photo} fallback={<BiUserCircle size="1.8rem" />} alt="" />
              {!collapse && (
                <Box textAlign="start">
                  <Box isTruncated fontWeight="semibold">
                    {user.firstName + " " + user.lastName}
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <>
              <SkeletonCircle size="7" />
              {!collapse && <Skeleton w="5rem" h="1rem"></Skeleton>}
            </>
          )}
        </>
      </HStack>
      {!collapse && (
        <Box fontSize="lg" color={mode("gray.800", "gray.400")}>
          <HiSelector />
        </Box>
      )}
    </Flex>
  );
};

const NavLink = (props) => {
  const { icon, isActive, label, collapse, ...rest } = props;
  return (
    <Tooltip label={collapse ? label : ""} fontSize="md" placement="right">
      <Link
        display="block"
        py="2"
        px="3"
        width="full"
        minW="max-content"
        borderRadius="md"
        transition="all 0.1s"
        fontWeight="medium"
        userSelect="none"
        aria-current={isActive ? "page" : undefined}
        color="gray.600"
        _hover={{
          bg: "brand.500",
          color: "white",
        }}
        _activeLink={{
          bg: "brand.500",
          color: "white",
        }}
        {...rest}
      >
        <HStack h="20px" spacing="5">
          <Box fontSize="18px">{icon}</Box>
          {!collapse && (
            <Text as="span">
              <Fade in={!collapse}>{label}</Fade>
            </Text>
          )}
        </HStack>
      </Link>
    </Tooltip>
  );
};
